<template>
  <div class="subfamilias-manager">
    
    <!-- Header y Controles -->
    <div class="manager-header">
      <div class="row align-items-center">
        <div class="col-md-6">
          <h4 class="manager-title">
            <i class="nc-icon nc-folder-17 mr-2"></i>
            Gestión de SubFamilias
          </h4>
          <small class="text-muted">
            Crear, editar y organizar las subfamilias de planes dentro de cada familia
          </small>
        </div>
        <div class="col-md-6 text-right">
          <button 
            class="btn btn-primary btn-sm mr-2" 
            @click="abrirModalCrear"
            :disabled="!familiaSeleccionada || loadingOperaciones.crear || guardando"
            :title="!familiaSeleccionada ? 'Selecciona una familia para crear una subfamilia' : 'Crear nueva subfamilia'"
          >
            <span v-if="loadingOperaciones.crear || guardando" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="nc-icon nc-simple-add mr-1"></i>
            Nueva SubFamilia
          </button>
          <button 
            class="btn btn-outline-secondary btn-sm" 
            @click="refreshSubFamilias"
            :disabled="loadingOperaciones.refrescar"
          >
            <span v-if="loadingOperaciones.refrescar" class="spinner-border spinner-border-sm mr-1"></span>
            <i v-else class="nc-icon nc-refresh-02 mr-1"></i>
            Refrescar
          </button>
        </div>
      </div>
      
      <!-- Selector de Familia -->
      <div class="familia-selector mt-3">
        <div class="row align-items-center">
          <div class="col-md-6">
            <div class="form-group mb-0">
              <label class="form-label">
                <i class="nc-icon nc-folder-17 mr-1"></i>
                Familia Seleccionada <span class="text-danger">*</span>
              </label>
              <select 
                class="form-control"
                v-model="familiaSeleccionada"
                @change="onFamiliaChange"
              >
                <option value="">-- Selecciona una familia para gestionar sus subfamilias --</option>
                <option 
                  v-for="familia in catalogos.familias" 
                  :key="familia._id"
                  :value="familia._id"
                  :disabled="!familia.activo"
                >
                  {{ familia.nombre }} (ID: {{ familia.id }})
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-6" v-if="familiaSeleccionada">
            <div class="familia-info">
              <h6 class="mb-1">
                <i class="nc-icon nc-check-2 text-success mr-1"></i>
                {{ getFamiliaSeleccionadaNombre() }}
              </h6>
              <small class="text-muted">
                ID: {{ getFamiliaSeleccionadaId() }} | 
                <span class="badge badge-primary">{{ subfamilias.length }} subfamilias</span>
              </small>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Banner de contexto de navegación -->
      <div v-if="contextoNavegacion && contextoNavegacion.origen === 'familias'" class="alert mt-3 mb-0" :class="contextoNavegacion.familiaOrigen.activo ? 'alert-primary' : 'alert-warning'">
        <div class="row align-items-center">
          <div class="col">
            <i class="nc-icon nc-direction mr-2"></i>
            <strong>Navegaste desde Familia:</strong> 
            <span class="familia-origen-badge" :class="{ 'text-danger': !contextoNavegacion.familiaOrigen.activo }">
              "{{ contextoNavegacion.familiaOrigen.nombre }}"
            </span>
            <span class="badge ml-2" :class="contextoNavegacion.familiaOrigen.activo ? 'badge-success' : 'badge-secondary'">
              {{ contextoNavegacion.familiaOrigen.activo ? 'Familia Activa' : 'Familia Inactiva' }}
            </span>
            <br>
            <small class="mt-1">
              Visualizando <strong>subfamilias de esta familia específica</strong>.
              <span v-if="!contextoNavegacion.familiaOrigen.activo" class="text-danger">
                Nota: Esta familia está inactiva, pero puedes gestionar sus subfamilias.
              </span>
            </small>
          </div>
          <div class="col-auto">
            <button class="btn btn-sm btn-outline-info" @click="limpiarContextoNavegacion">
              <i class="nc-icon nc-zoom-split mr-1"></i>
              Ver Todas
            </button>
          </div>
        </div>
      </div>
      
      <!-- Información sobre filtros (solo cuando no hay contexto) -->
      <div v-else-if="!familiaSeleccionada" class="alert alert-info mt-3 mb-0">
        <i class="nc-icon nc-bulb-63 mr-2"></i>
        <strong>Vista Global:</strong> Visualizando todas las subfamilias del sistema. Selecciona una familia para filtrar por familia específica.
      </div>
      <div v-else class="alert alert-success mt-3 mb-0">
        <i class="nc-icon nc-check-2 mr-2"></i>
        <strong>Filtrado por Familia:</strong> Visualizando subfamilias de "{{ getFamiliaSeleccionadaNombre() }}".
        <button class="btn btn-sm btn-outline-success ml-2" @click="verTodasLasSubfamilias">
          <i class="nc-icon nc-zoom-split mr-1"></i>Ver Todas
        </button>
      </div>
    </div>

    <!-- Contenido Principal -->
    <div>
      <!-- Filtros y Búsqueda -->
      <div class="filters-section mb-4">
        <div class="card">
          <div class="card-body py-3">
            <div class="row align-items-center">
              <div class="col-md-4">
                <div class="form-group mb-0">
                  <label class="form-label">Buscar SubFamilia</label>
                  <input 
                    type="text" 
                    class="form-control form-control-sm"
                    v-model="filtros.nombre"
                    @input="debounceFilter"
                    placeholder="Nombre de la subfamilia..."
                  >
                </div>
              </div>
              <div class="col-md-2">
                <div class="form-group mb-0">
                  <label class="form-label">Estado</label>
                  <select 
                    class="form-control form-control-sm"
                    v-model="filtros.activo"
                    @change="aplicarFiltros"
                  >
                    <option value="">Todos</option>
                    <option value="true">Activos</option>
                    <option value="false">Inactivos</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group mb-0">
                  <label class="form-label">Fecha Creación</label>
                  <input 
                    type="date" 
                    class="form-control form-control-sm"
                    v-model="filtros.fechaCreacion"
                    @change="aplicarFiltros"
                    title="Filtrar subfamilias creadas desde esta fecha"
                  >
                </div>
              </div>
              <div class="col-md-2">
                <div class="form-group mb-0">
                  <label class="form-label">&nbsp;</label>
                  <button 
                    class="btn btn-outline-secondary btn-sm btn-block"
                    @click="limpiarFiltros"
                  >
                    <i class="nc-icon nc-simple-remove"></i>
                    Limpiar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabla de SubFamilias -->
      <div class="table-section">
        <div class="card">
          <div class="card-header">
            <div class="row align-items-center">
              <div class="col">
                <h6 class="card-title mb-0">
                  <span v-if="familiaSeleccionada">
                    Lista de SubFamilias - {{ getFamiliaSeleccionadaNombre() }}
                  </span>
                  <span v-else>
                    Lista de SubFamilias - Todas las Familias
                  </span>
                  <span class="badge badge-secondary ml-2">
                    {{ subfamilias.length }} registros
                  </span>
                </h6>
              </div>
            </div>
          </div>
          
          <div class="card-body p-0">
            
            <!-- Loading -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Cargando subfamilias...</span>
              </div>
              <p class="mt-2 mb-0 text-muted">Cargando subfamilias...</p>
            </div>
            
            <!-- Tabla con datos -->
            <div v-else-if="subfamilias && subfamilias.length > 0" class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>
                      <button class="btn-sort" @click="sortBy('id')">
                        ID 
                        <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'id' && ordenamiento.direccion === 'asc'"></i>
                        <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'id' && ordenamiento.direccion === 'desc'"></i>
                      </button>
                    </th>
                    <th>
                      <button class="btn-sort" @click="sortBy('nombre')">
                        Nombre
                        <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'nombre' && ordenamiento.direccion === 'asc'"></i>
                        <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'nombre' && ordenamiento.direccion === 'desc'"></i>
                      </button>
                    </th>
                    <th>
                      <button class="btn-sort" @click="sortBy('createdAt')">
                        Fecha Creación
                        <i class="nc-icon nc-minimal-up" v-if="ordenamiento.campo === 'createdAt' && ordenamiento.direccion === 'asc'"></i>
                        <i class="nc-icon nc-minimal-down" v-if="ordenamiento.campo === 'createdAt' && ordenamiento.direccion === 'desc'"></i>
                      </button>
                    </th>
                    <th>Estado</th>
                    <th>Usuario</th>
                    <th width="140">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="subfamilia in subfamilias" :key="subfamilia._id">
                    <td>
                      <span class="badge badge-outline-info">{{ subfamilia.id }}</span>
                    </td>
                    <td>
                      <div class="subfamilia-info">
                        <strong 
                          class="subfamilia-nombre-clickeable" 
                          @click="navegarAPlanes(subfamilia)"
                          :title="'Ver planes de ' + subfamilia.nombre"
                        >
                          {{ subfamilia.nombre }}
                        </strong>
                        <br>
                        <small class="text-muted">
                          <span v-if="!familiaSeleccionada">
                            <strong>Familia:</strong> 
                            <span class="familia-badge" :class="{ 'text-danger': !subfamilia.familia && !(subfamilia.familiaInfo && subfamilia.familiaInfo.nombre) }">
                              {{ (subfamilia.familia && subfamilia.familia.nombre) || (subfamilia.familiaInfo && subfamilia.familiaInfo.nombre) || 'Sin Familia Asignada' }}
                            </span>
                            <span v-if="(subfamilia.familia && subfamilia.familia.id) || (subfamilia.familiaInfo && subfamilia.familiaInfo.id)" class="text-muted">
                              (ID: {{ (subfamilia.familia && subfamilia.familia.id) || (subfamilia.familiaInfo && subfamilia.familiaInfo.id) }})
                            </span>
                            |
                          </span>
                          <span v-else>
                            Familia: {{ getFamiliaSeleccionadaNombre() }} |
                          </span>
                          <span 
                            class="planes-link" 
                            @click="navegarAPlanes(subfamilia)"
                            :title="'Ver planes'"
                          >
                            Ver planes
                          </span>
                        </small>
                      </div>
                    </td>
                    <td>
                      <small>{{ subfamilia.fechaCreacionFormatted || formatearFecha(subfamilia.createdAt) }}</small>
                    </td>
                    <td>
                      <span 
                        class="badge"
                        :class="subfamilia.activo ? 'badge-success' : 'badge-secondary'"
                      >
                        {{ subfamilia.activo ? 'Activo' : 'Inactivo' }}
                      </span>
                    </td>
                    <td>
                      <small>{{ subfamilia.nombreUsuarioCreacion || 'N/A' }}</small>
                    </td>
                    <td>
                      <div class="btn-group btn-group-sm">
                        <button 
                          class="btn btn-outline-success"
                          @click="navegarAPlanes(subfamilia)"
                          :title="'Ver planes de ' + subfamilia.nombre"
                        >
                          <i class="nc-icon nc-settings-gear-65"></i>
                        </button>
                        <button 
                          class="btn btn-outline-info"
                          @click="verDetalle(subfamilia)"
                          :disabled="loadingOperaciones.verDetalle === subfamilia.id"
                          :title="'Ver detalles de ' + subfamilia.nombre"
                        >
                          <span v-if="loadingOperaciones.verDetalle === subfamilia.id" class="spinner-border spinner-border-sm"></span>
                          <i v-else class="nc-icon nc-zoom-split"></i>
                        </button>
                        <button 
                          class="btn btn-outline-primary"
                          @click="editarSubFamilia(subfamilia)"
                          :disabled="loadingOperaciones.editar === subfamilia.id"
                          :title="'Editar ' + subfamilia.nombre"
                        >
                          <span v-if="loadingOperaciones.editar === subfamilia.id" class="spinner-border spinner-border-sm"></span>
                          <i v-else class="nc-icon nc-ruler-pencil"></i>
                        </button>
                        <button 
                          class="btn btn-outline-danger"
                          @click="eliminarSubFamilia(subfamilia)"
                          :title="subfamilia.activo ? 'Eliminar (Etapa 1): ' + subfamilia.nombre : 'Eliminar Permanentemente (Etapa 2): ' + subfamilia.nombre"
                          :disabled="loadingOperaciones.eliminar === subfamilia.id"
                        >
                          <span v-if="loadingOperaciones.eliminar === subfamilia.id" class="spinner-border spinner-border-sm"></span>
                          <i v-else class="nc-icon nc-simple-remove"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Sin Resultados -->
            <div v-else class="empty-state text-center py-5">
              <i class="nc-icon nc-folder-17 empty-icon"></i>
              <h5>No hay subfamilias</h5>
              <p class="text-muted" v-if="familiaSeleccionada">
                No se encontraron subfamilias para la familia "{{ getFamiliaSeleccionadaNombre() }}"
                <span v-if="contextoNavegacion && !contextoNavegacion.familiaOrigen.activo" class="text-warning"> (Familia Inactiva)</span>.
              </p>
              <p class="text-muted" v-else>
                No se encontraron subfamilias en el sistema con los filtros aplicados.
              </p>
              <div class="mt-3">
                <button v-if="familiaSeleccionada" class="btn btn-primary mr-2" @click="abrirModalCrear">
                  <i class="nc-icon nc-simple-add mr-1"></i>
                  Crear primera subfamilia
                </button>
                <button v-else class="btn btn-info mr-2" @click="emitirCambiarTab">
                  <i class="nc-icon nc-folder-17 mr-1"></i>
                  Ir a Familias
                </button>
                <button v-if="contextoNavegacion" class="btn btn-outline-info mr-2" @click="limpiarContextoNavegacion">
                  <i class="nc-icon nc-zoom-split mr-1"></i>
                  Ver Todas las SubFamilias
                </button>
                <button class="btn btn-outline-secondary" @click="limpiarFiltros">
                  <i class="nc-icon nc-simple-remove mr-1"></i>
                  Limpiar Filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <div v-if="pagination.totalPages > 1" class="pagination-section mt-4">
        <nav>
          <ul class="pagination justify-content-center">
            <li class="page-item" :class="{ disabled: !pagination.hasPrev }">
              <button 
                class="page-link" 
                @click="cambiarPagina(pagination.prevPage)"
                :disabled="!pagination.hasPrev"
              >
                <i class="nc-icon nc-minimal-left"></i>
              </button>
            </li>
            
            <li 
              v-for="pagina in paginasVisibles" 
              :key="pagina"
              class="page-item" 
              :class="{ active: pagina === pagination.page }"
            >
              <button class="page-link" @click="cambiarPagina(pagina)">
                {{ pagina }}
              </button>
            </li>
            
            <li class="page-item" :class="{ disabled: !pagination.hasNext }">
              <button 
                class="page-link" 
                @click="cambiarPagina(pagination.nextPage)"
                :disabled="!pagination.hasNext"
              >
                <i class="nc-icon nc-minimal-right"></i>
              </button>
            </li>
          </ul>
        </nav>
        
        <div class="pagination-info text-center mt-2">
          <small class="text-muted">
            Mostrando {{ (pagination.page - 1) * pagination.limit + 1 }} a 
            {{ Math.min(pagination.page * pagination.limit, pagination.totalRecords) }} 
            de {{ pagination.totalRecords }} registros
          </small>
        </div>
      </div>
    </div>

    <!-- Modal Crear/Editar SubFamilia -->
    <div class="modal fade" ref="modalSubFamilia" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="nc-icon nc-folder-17 mr-2"></i>
              {{ modoEdicion ? 'Editar SubFamilia' : 'Nueva SubFamilia' }}
            </h5>
            <button type="button" class="close" @click="cerrarModal">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          
          <div class="modal-body">
            <form @submit.prevent="guardarSubFamilia">
              <div class="form-group">
                <label class="form-label">
                  Familia <span class="text-danger">*</span>
                </label>
                <input 
                  type="text" 
                  class="form-control"
                  :value="getFamiliaSeleccionadaNombre()"
                  readonly
                  disabled
                >
                <small class="form-text text-muted">
                  La subfamilia se creará dentro de esta familia
                </small>
              </div>
              
              <div class="form-group">
                <label for="nombreSubFamilia" class="form-label">
                  Nombre de la SubFamilia <span class="text-danger">*</span>
                </label>
                <input 
                  type="text" 
                  class="form-control"
                  id="nombreSubFamilia"
                  v-model="formSubFamilia.nombre"
                  placeholder="Ej: Mensual, Trimestral, Anual..."
                  required
                  maxlength="100"
                  :disabled="guardando"
                >
                <small class="form-text text-muted">
                  Máximo 100 caracteres. Debe ser único dentro de la familia.
                </small>
              </div>
              
              <!-- Campo solo visible en modo edición -->
              <div v-if="modoEdicion" class="form-group">
                <div class="custom-control custom-switch">
                  <input 
                    type="checkbox" 
                    class="custom-control-input" 
                    id="actualizarPlanes"
                    v-model="formSubFamilia.actualizarPlanes"
                    :disabled="guardando"
                  >
                  <label class="custom-control-label" for="actualizarPlanes">
                    <strong>Actualizar Planes en Cascada</strong>
                  </label>
                </div>
                <small class="form-text text-muted">
                  <i class="nc-icon nc-alert-circle-i mr-1"></i>
                  Si está activo, se actualizará el nombre de la subfamilia en todos los planes asociados.
                </small>
              </div>
            </form>
          </div>
          
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cerrarModal">
              Cancelar
            </button>
            <button 
              type="button" 
              class="btn btn-primary"
              @click="guardarSubFamilia"
              :disabled="guardando || !formSubFamilia.nombre.trim()"
            >
              <span v-if="guardando" class="spinner-border spinner-border-sm mr-2"></span>
              {{ guardando ? 'Guardando...' : (modoEdicion ? 'Actualizar' : 'Crear') }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'SubFamiliasManager',
  props: {
    catalogos: {
      type: Object,
      required: true,
      default: () => ({
        familias: [],
        subfamilias: []
      })
    },
    familiaPreseleccionada: {
      type: Object,
      default: null
    },
    contextoNavegacion: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      guardando: false,
      loadingOperaciones: {
        crear: false,
        editar: null,
        eliminar: null,
        verDetalle: null,
        refrescar: false
      },
      subfamilias: [],
      familiaSeleccionada: '',
      
      // Paginación
      pagination: {
        page: 1,
        limit: 10,
        totalPages: 1,
        totalRecords: 0,
        hasNext: false,
        hasPrev: false,
        nextPage: null,
        prevPage: null
      },
      
      // Ordenamiento
      ordenamiento: {
        campo: 'nombre',
        direccion: 'asc'
      },
      
      // Filtros
      filtros: {
        nombre: '',
        activo: '',
        fechaCreacion: ''
      },
      
      // Modal y formulario
      modoEdicion: false,
      subfamiliaSeleccionada: null,
      formSubFamilia: {
        nombre: '',
        actualizarPlanes: true  // Por defecto activado para actualización en cascada
      },
      
      // Debounce timer
      debounceTimer: null
    }
  },
  computed: {
    hayFamiliasDisponibles() {
      return this.catalogos.familias && this.catalogos.familias.length > 0
    },
    
    paginasVisibles() {
      const total = this.pagination.totalPages
      const actual = this.pagination.page
      const visibles = []
      
      let inicio = Math.max(1, actual - 2)
      let fin = Math.min(total, actual + 2)
      
      for (let i = inicio; i <= fin; i++) {
        visibles.push(i)
      }
      
      return visibles
    }
  },
  watch: {
    familiaPreseleccionada: {
      immediate: true,
      handler(nuevaFamilia) {
        // Solo preseleccionar si NO hay contexto de navegación desde familias
        if (nuevaFamilia && nuevaFamilia._id && !this.contextoNavegacion) {
          console.log('🎯 Familia preseleccionada recibida:', nuevaFamilia)
          this.familiaSeleccionada = nuevaFamilia._id
          console.log('✅ Familia seleccionada:', nuevaFamilia._id)
        }
      }
    },
    
    familiaSeleccionada: {
      handler(nuevaFamilia) {
        // Siempre cargar subfamilias, con o sin filtro de familia
        this.cargarSubFamilias()
      }
    },
    
    contextoNavegacion: {
      immediate: true,
      handler(nuevoContexto) {
        if (nuevoContexto && nuevoContexto.origen === 'familias') {
          console.log('🔄 Contexto de navegación detectado:', nuevoContexto.familiaOrigen.nombre)
          // Filtrar por la familia de origen, independientemente de si está activa o inactiva
          this.familiaSeleccionada = nuevoContexto.familiaOrigen._id
          // Cargar subfamilias de esa familia específica
          this.cargarSubFamilias()
        }
      }
    }
  },
  mounted() {
    // Verificar contexto de navegación primero
    if (this.contextoNavegacion && this.contextoNavegacion.origen === 'familias') {
      console.log('🔄 Montando con contexto de navegación desde:', this.contextoNavegacion.familiaOrigen.nombre)
      // Filtrar por familia de origen (activa o inactiva)
      this.familiaSeleccionada = this.contextoNavegacion.familiaOrigen._id
      this.cargarSubFamilias()
    } else if (this.familiaPreseleccionada && this.familiaPreseleccionada._id) {
      // Aplicar familia preseleccionada solo si no hay contexto de navegación
      console.log('🎯 Aplicando familia preseleccionada al montar:', this.familiaPreseleccionada.nombre)
      this.familiaSeleccionada = this.familiaPreseleccionada._id
    } else {
      // Si no hay familia preseleccionada ni contexto, cargar todas las subfamilias
      console.log('📊 Cargando todas las subfamilias del sistema')
      this.cargarSubFamilias()
    }
  },
  methods: {
    // Navegación y selección
    onFamiliaChange() {
      console.log('📋 Familia cambiada:', this.familiaSeleccionada)
      // Limpiar filtros y resetear paginación cuando cambia la familia
      this.limpiarFiltros()
      this.pagination.page = 1
    },

    verTodasLasSubfamilias() {
      console.log('🌐 Cambiando a vista global - todas las subfamilias')
      this.familiaSeleccionada = ''
      this.limpiarFiltros()
      this.pagination.page = 1
    },

    getFiltrosActivos() {
      const filtros = {}
      
      if (this.filtros.nombre.trim()) {
        filtros.nombre = this.filtros.nombre.trim()
      }
      
      if (this.filtros.activo !== '') {
        filtros.activo = this.filtros.activo === 'true'
      }
      
      if (this.filtros.fechaCreacion) {
        filtros.fechaCreacion = this.filtros.fechaCreacion
      }
      
      return filtros
    },

    // Carga de datos
    async cargarSubFamilias() {
      try {
        this.loading = true
        
        const parametros = {
          page: this.pagination.page,
          limit: this.pagination.limit,
          sortBy: this.ordenamiento.campo,
          sortOrder: this.ordenamiento.direccion,
          ...this.getFiltrosActivos()
        }
        
        // Solo agregar filtro de familia si hay una seleccionada
        if (this.familiaSeleccionada) {
          parametros.familia = this.familiaSeleccionada
          console.log('📊 Cargando subfamilias para familia:', this.familiaSeleccionada)
        } else {
          console.log('📊 Cargando todas las subfamilias del sistema')
        }
        
        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('jCxbraoQeD')}`
          },
          data: {
            Header: {
              Operacion: '/v1/planes/subfamilia/consultar-paginadas'
            },
            Auth: {
              Token: sessionStorage.getItem('jCxbraoQeD')
            },
            Parameters: parametros,
            Body: {}
          }
        })

        console.log('📊 Respuesta subfamilias:', response.data)

        if (response.data && response.data.dataOut && response.data.dataOut.subfamilias) {
          this.subfamilias = response.data.dataOut.subfamilias
          this.pagination = response.data.dataOut.pagination || this.pagination
          
          // Emitir estadísticas actualizadas
          this.$emit('actualizarEstadisticas', {
            totalSubFamilias: this.pagination.totalRecords
          })
          
          console.log('✅ Subfamilias cargadas:', this.subfamilias.length)
        } else {
          console.warn('⚠️ No se encontraron subfamilias en la respuesta')
          this.subfamilias = []
        }
        
      } catch (error) {
        console.error('❌ Error al cargar subfamilias:', error)
        
        // Manejo específico de errores
        let errorMessage = 'No se pudieron cargar las subfamilias'
        
        if (error.response && error.response.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          setTimeout(() => {
            sessionStorage.clear()
            this.$router.push('/login')
          }, 2000)
        } else if (error.response && error.response.status === 400) {
          errorMessage = error.response.data && error.response.data.result && error.response.data.result.message || 'Error en los parámetros de la consulta'
        } else if (error.response && error.response.status === 500) {
          errorMessage = 'Error interno del servidor. Inténtalo nuevamente.'
        }
        
        this.$swal.fire({
          title: 'Error al Cargar SubFamilias',
          text: errorMessage,
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 4000,
          showConfirmButton: false
        })
        
        this.subfamilias = []
      } finally {
        this.loading = false
      }
    },

    async refreshSubFamilias() {
      try {
        this.loadingOperaciones.refrescar = true
        await this.cargarSubFamilias()
        this.$emit('refresh')
        
        this.$swal.fire({
          title: 'Actualizado',
          text: 'Las subfamilias han sido actualizadas',
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-success',
          allowOutsideClick: false,
          allowEscapeKey: false
        })
      } catch (error) {
        console.error('Error al refrescar subfamilias:', error)
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudo refrescar la lista de subfamilias',
          icon: 'error',
          toast: true,
          position: 'top-end',
          timer: 3000,
          showConfirmButton: false
        })
      } finally {
        this.loadingOperaciones.refrescar = false
      }
    },

    // Filtros y ordenamiento
    debounceFilter() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.aplicarFiltros()
      }, 500)
    },

    async aplicarFiltros() {
      this.pagination.page = 1
      await this.cargarSubFamilias()
    },

    limpiarFiltros() {
      this.filtros = {
        nombre: '',
        activo: '',
        fechaCreacion: ''
      }
      this.aplicarFiltros()
    },

    async sortBy(campo) {
      if (this.ordenamiento.campo === campo) {
        this.ordenamiento.direccion = this.ordenamiento.direccion === 'asc' ? 'desc' : 'asc'
      } else {
        this.ordenamiento.campo = campo
        this.ordenamiento.direccion = 'asc'
      }
      
      await this.cargarSubFamilias()
    },

    async cambiarPagina(pagina) {
      if (pagina && pagina !== this.pagination.page) {
        this.pagination.page = pagina
        await this.cargarSubFamilias()
      }
    },

    // CRUD Operations
    abrirModalCrear() {
      console.log('🎯 Abriendo modal para crear nueva subfamilia')
      
      // Validar que haya una familia seleccionada
      if (!this.familiaSeleccionada) {
        this.$swal.fire({
          title: 'Familia Requerida',
          text: 'Debe seleccionar una familia antes de crear una subfamilia',
          icon: 'warning',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-warning'
        })
        return
      }
      
      this.modoEdicion = false
      this.subfamiliaSeleccionada = null
      this.formSubFamilia = {
        nombre: '',
        actualizarPlanes: true
      }
      
      // Pequeño delay para asegurar que el DOM está listo
      this.$nextTick(() => {
        this.mostrarModal()
      })
    },

    editarSubFamilia(subfamilia) {
      this.loadingOperaciones.editar = subfamilia.id
      
      this.modoEdicion = true
      this.subfamiliaSeleccionada = subfamilia
      this.formSubFamilia = {
        nombre: subfamilia.nombre,
        actualizarPlanes: true  // Por defecto activado
      }
      
      this.$nextTick(() => {
        this.mostrarModal()
        this.loadingOperaciones.editar = null
      })
    },

    cerrarModal() {
      const modalElement = this.$refs.modalSubFamilia;
      if (modalElement) {
        if (window.bootstrap && window.bootstrap.Modal) {
          // Bootstrap 5 nativo
          const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
          if (modalInstance) {
            modalInstance.hide();
          }
        } else {
          // Fallback manual con JavaScript puro
          modalElement.style.display = 'none';
          modalElement.classList.remove('show');
          document.body.classList.remove('modal-open');
          
          // Remover backdrop manual
          const backdrop = document.getElementById('modal-backdrop-subfamilia');
          if (backdrop) {
            backdrop.remove();
          }
        }
      }
      
      // Limpiar formulario
      this.formSubFamilia = {
        nombre: '',
        actualizarPlanes: true
      };
      this.modoEdicion = false;
      this.subfamiliaSeleccionada = null;
    },

    async guardarSubFamilia() {
      if (!this.formSubFamilia.nombre.trim()) {
        this.$swal.fire({
          title: 'Campo Requerido',
          text: 'El nombre de la subfamilia es obligatorio',
          icon: 'warning',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-warning'
        })
        return
      }

      if (!this.familiaSeleccionada) {
        this.$swal.fire({
          title: 'Error',
          text: 'Debe seleccionar una familia antes de crear la subfamilia',
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-danger'
        })
        return
      }

      try {
        this.guardando = true

        console.log('📝 Guardando subfamilia:', {
          modo: this.modoEdicion ? 'edición' : 'creación',
          nombre: this.formSubFamilia.nombre.trim(),
          familia: this.familiaSeleccionada,
          actualizarPlanes: this.modoEdicion ? this.formSubFamilia.actualizarPlanes : 'N/A'
        })

        const operacion = this.modoEdicion ? '/v1/planes/subfamilia/actualizar' : '/v1/planes/subfamilia/crear'
        
        // Mapeo correcto de campos según especificación del endpoint
        let parameters = {}
        let body = {}
        
        if (this.modoEdicion) {
          // Modo edición: Parameters contiene familia e idSubFamilia
          parameters = {
            familia: this.familiaSeleccionada,
            idSubFamilia: this.subfamiliaSeleccionada.id
          }
          // Body contiene nuevo nombre y bandera de actualización en cascada
          body = {
            nombre: this.formSubFamilia.nombre.trim(),
            actualizarPlanes: this.formSubFamilia.actualizarPlanes || false
          }
        } else {
          // Modo creación: Parameters vacío
          parameters = {}
          // Body contiene nombre y familia
          body = {
            nombre: this.formSubFamilia.nombre.trim(),
            familia: this.familiaSeleccionada
          }
        }

        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('jCxbraoQeD')}`
          },
          data: {
            Header: { Operacion: operacion },
            Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
            Parameters: parameters,
            Body: body
          }
        })

        console.log('📝 Respuesta del servidor:', response.data)

        // Verificar respuesta exitosa según la especificación
        if (response.data?.result?.code === 0 || response.data?.result?.httpCode === 200) {
          const subfamiliaCreada = response.data?.dataOut?.subfamilia
          const mensaje = response.data?.dataOut?.mensaje || `Subfamilia ${this.modoEdicion ? 'actualizada' : 'creada'} exitosamente`
          const actualizacionCascada = response.data?.dataOut?.actualizacionCascada
          
          // Construir información adicional sobre actualización en cascada
          let infoCascada = ''
          if (this.modoEdicion && actualizacionCascada) {
            const planesActualizados = actualizacionCascada.planesActualizados || 0
            const mensajeCascada = actualizacionCascada.mensaje || ''
            
            if (planesActualizados > 0) {
              infoCascada = `
                <div class="alert alert-info text-left" style="margin: 15px 0; background-color: #d1ecf1; border-color: #17a2b8; color: #0c5460;">
                  <strong style="color: #0c5460;">🔄 Actualización en Cascada:</strong>
                  <ul style="margin: 5px 0 0 20px; color: #0c5460;">
                    <li><strong>${planesActualizados} plan${planesActualizados > 1 ? 'es' : ''}</strong> actualizado${planesActualizados > 1 ? 's' : ''} con el nuevo nombre</li>
                  </ul>
                  <small style="color: #0c5460; opacity: 0.8;">${mensajeCascada}</small>
                </div>
              `
            } else if (this.formSubFamilia.actualizarPlanes) {
              infoCascada = `
                <div class="alert alert-warning text-left" style="margin: 15px 0; background-color: #fff3cd; border-color: #ffc107; color: #856404;">
                  <strong style="color: #856404;">ℹ️ Sin planes vinculados:</strong>
                  <p style="margin: 5px 0 0 0; font-size: 0.9rem; color: #856404;">No se encontraron planes asociados a esta subfamilia para actualizar.</p>
                </div>
              `
            }
          }
          
          this.$swal.fire({
            title: '¡Éxito!',
            html: `
              <div class="text-center">
                <i class="nc-icon nc-check-2" style="font-size: 3rem; color: #6bd098; margin-bottom: 15px;"></i>
                <h5 style="color: #28a745; margin-bottom: 15px;">¡SubFamilia ${this.modoEdicion ? 'Actualizada' : 'Creada'}!</h5>
                
                <div class="alert alert-success text-left" style="margin: 15px 0;">
                  <strong>✅ ${this.modoEdicion ? 'SubFamilia actualizada:' : 'Nueva subfamilia creada:'}</strong>
                  <ul style="margin: 5px 0 0 20px;">
                    <li><strong>Nombre:</strong> "${subfamiliaCreada ? subfamiliaCreada.nombre : this.formSubFamilia.nombre}"</li>
                    <li><strong>ID:</strong> ${subfamiliaCreada ? subfamiliaCreada.id : 'N/A'}</li>
                    <li><strong>Familia:</strong> ${this.getFamiliaSeleccionadaNombre()}</li>
                  </ul>
                </div>
                
                ${infoCascada}
                
                <p class="text-muted" style="font-size: 0.9rem;">
                  ${mensaje}
                </p>
              </div>
            `,
            icon: 'success',
            showConfirmButton: true,
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-success',
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              container: 'swal-subfamilia-exitosa'
            }
          })

          this.cerrarModal()
          await this.cargarSubFamilias()
          
          // Emitir evento para actualizar estadísticas
          this.$emit('refresh')
          
        } else if (response.data?.result?.code === 400) {
          // Error de validación general
          const errorMessage = response.data.result.message || 
                             response.data.result.errors?.[0] || 
                             'Error al procesar la solicitud'
          
          this.$swal.fire({
            title: 'Error de Validación',
            text: errorMessage,
            icon: 'warning',
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-warning'
          })
        } else if (response.data?.result?.code === 409) {
          // Error específico: SubFamilia ya existe (Conflict)
          const errorMessage = response.data.result.message || 
                             response.data.result.errors?.[0] || 
                             'Ya existe una subfamilia con este nombre en la familia seleccionada'
          
          this.$swal.fire({
            title: 'SubFamilia Duplicada',
            html: `
              <div class="text-center">
                <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #f39c12; margin-bottom: 15px;"></i>
                <p style="font-size: 1.1rem; margin-bottom: 15px;">
                  <strong>${errorMessage}</strong>
                </p>
                <div style="margin: 15px 0; padding: 15px; background: rgba(243, 156, 18, 0.1); border-radius: 8px; border-left: 4px solid #f39c12;">
                  <p style="margin: 0; font-size: 0.9rem; text-align: left;">
                    <strong>📋 Detalles del conflicto:</strong><br>
                    • <strong>Familia:</strong> ${this.getFamiliaSeleccionadaNombre()}<br>
                    • <strong>Nombre ingresado:</strong> "${this.formSubFamilia.nombre.trim()}"<br>
                    • <strong>Restricción:</strong> Nombres únicos por familia
                  </p>
                </div>
                <p class="text-muted" style="font-size: 0.9rem;">
                  Por favor, elige un nombre diferente para la subfamilia o edita la subfamilia existente.
                </p>
              </div>
            `,
            icon: 'warning',
            confirmButtonText: 'Cambiar Nombre',
            confirmButtonClass: 'btn btn-warning',
            allowOutsideClick: false,
            customClass: {
              container: 'swal-subfamilia-duplicada'
            }
          }).then(() => {
            // Mantener el modal abierto y enfocar el campo de nombre
            this.$nextTick(() => {
              const inputNombre = document.getElementById('nombreSubFamilia')
              if (inputNombre) {
                inputNombre.focus()
                inputNombre.select()
              }
            })
          })
          
          // No cerrar el modal para que el usuario pueda corregir el nombre
          return
          
        } else {
          // Otros errores
          throw new Error(response.data?.result?.message || 'Error desconocido del servidor')
        }

      } catch (error) {
        console.error('❌ Error al guardar subfamilia:', error)
        console.log('📊 Detalles del error:', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message
        })
        
        let errorMessage = 'No se pudo guardar la subfamilia'
        let errorTitle = 'Error'
        
        if (error.response?.status === 400) {
          errorMessage = error.response.data?.result?.message || 
                        error.response.data?.result?.errors?.[0] ||
                        'Error de validación en los datos enviados'
          errorTitle = 'Error de Validación'
        } else if (error.response?.status === 409) {
          // Manejo específico para conflicto (subfamilia duplicada)
          const serverMessage = error.response.data?.result?.message || 
                               error.response.data?.result?.errors?.[0] ||
                               'Ya existe una subfamilia con este nombre en la familia seleccionada'
          
          this.$swal.fire({
            title: 'SubFamilia Duplicada',
            html: `
              <div class="text-center">
                <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #f39c12; margin-bottom: 15px;"></i>
                <p style="font-size: 1.1rem; margin-bottom: 15px;">
                  <strong>${serverMessage}</strong>
                </p>
                <div style="margin: 15px 0; padding: 15px; background: rgba(243, 156, 18, 0.1); border-radius: 8px; border-left: 4px solid #f39c12;">
                  <p style="margin: 0; font-size: 0.9rem; text-align: left;">
                    <strong>📋 Detalles del conflicto:</strong><br>
                    • <strong>Familia:</strong> ${this.getFamiliaSeleccionadaNombre()}<br>
                    • <strong>Nombre ingresado:</strong> "${this.formSubFamilia.nombre.trim()}"<br>
                    • <strong>Restricción:</strong> Nombres únicos por familia
                  </p>
                </div>
                <p class="text-muted" style="font-size: 0.9rem;">
                  Por favor, elige un nombre diferente para la subfamilia o edita la subfamilia existente.
                </p>
              </div>
            `,
            icon: 'warning',
            confirmButtonText: 'Cambiar Nombre',
            confirmButtonClass: 'btn btn-warning',
            allowOutsideClick: false,
            customClass: {
              container: 'swal-subfamilia-duplicada'
            }
          }).then(() => {
            // Mantener el modal abierto y enfocar el campo de nombre
            this.$nextTick(() => {
              const inputNombre = document.getElementById('nombreSubFamilia')
              if (inputNombre) {
                inputNombre.focus()
                inputNombre.select()
              }
            })
          })
          
          // Salir del catch sin mostrar el modal de error genérico
          return
          
        } else if (error.response?.status === 401) {
          errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
          errorTitle = 'Sesión Expirada'
          // Opcional: redirigir al login
          setTimeout(() => {
            sessionStorage.clear()
            this.$router.push('/login')
          }, 2000)
        } else if (error.response?.status >= 500) {
          errorMessage = 'Error interno del servidor. Por favor, intenta nuevamente en unos momentos.'
          errorTitle = 'Error del Servidor'
        } else if (error.message) {
          errorMessage = error.message
        }

        this.$swal.fire({
          title: errorTitle,
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'Entendido',
          confirmButtonClass: 'btn btn-danger'
        })
      } finally {
        this.guardando = false
      }
    },

    async eliminarSubFamilia(subfamilia) {
      // Activar loading inmediatamente cuando se hace clic en eliminar
      this.loadingOperaciones.eliminar = subfamilia.id

      // Primero consultar información de la subfamilia para conocer dependencias
      let planesVinculados = 0
      let planesActivosDetalle = []
      let puedeEliminar = true
      
      try {
        // Consultar detalle de la subfamilia para obtener estadísticas y metadata
        const response = await this.$CentralGymOPS_API({
          method: 'POST',
          url: '/',
          headers: {
            'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
          },
          data: {
            Header: { Operacion: '/v1/planes/subfamilia/consultar-por-id' },
            Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
            Parameters: { id: subfamilia.id },
            Body: {}
          }
        })

        console.log('🔍 Respuesta consulta subfamilia por ID:', response.data)

        if (response.data?.dataOut?.subfamilia?.estadisticas) {
          planesVinculados = response.data.dataOut.subfamilia.estadisticas.planesVinculados || 0
        }
        
        if (response.data?.dataOut?.subfamilia?.metadata) {
          puedeEliminar = response.data.dataOut.subfamilia.metadata.puedeEliminar !== false
        }
        
        // Si es una subfamilia inactiva (etapa 2), consultar planes activos específicos haciendo un test de eliminación
        if (!subfamilia.activo && planesVinculados > 0) {
          try {
            const testResponse = await this.$CentralGymOPS_API({
              method: 'POST',
              url: '/',
              headers: {
                'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
              },
              data: {
                Header: { Operacion: '/v1/planes/subfamilia/eliminar' },
                Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
                Parameters: { id: subfamilia.id },
                Body: {}
              }
            })
          } catch (testError) {
            // Si el error es 409, extraer los planes activos
            if (testError.response?.status === 409 && testError.response.data?.dataOut?.planesActivos) {
              planesActivosDetalle = testError.response.data.dataOut.planesActivos || []
              console.log('📋 Planes activos obtenidos para confirmación:', planesActivosDetalle)
            }
          }
        }
        
      } catch (error) {
        console.warn('No se pudieron obtener estadísticas de la subfamilia:', error)
        // Continuar con la eliminación aunque no tengamos estadísticas
      }

      // Crear mensaje dinámico basado en las dependencias
      let mensajePlanes = ''
      let listaPlanesDetalles = ''
      
      if (planesVinculados > 0) {
        mensajePlanes = `<li><strong>${planesVinculados} plan${planesVinculados > 1 ? 'es' : ''}</strong> puede${planesVinculados > 1 ? 'n' : ''} verse afectado${planesVinculados > 1 ? 's' : ''}</li>`
      }
      
      // Si tenemos planes activos detallados (subfamilia inactiva - etapa 2), construir la lista
      if (planesActivosDetalle.length > 0) {
        const planesParaMostrar = planesActivosDetalle.slice(0, 5)
        planesParaMostrar.forEach(plan => {
          listaPlanesDetalles += `
            <div style="margin: 8px 0; padding: 8px; background: rgba(220, 53, 69, 0.1); border-radius: 4px; border-left: 3px solid #dc3545;">
              <strong>${plan.descripcion}</strong><br>
              <small>ID: ${plan.id} | Familia: ${plan.familia} | Valor: $${plan.valor}</small>
            </div>
          `
        })
        
        if (planesActivosDetalle.length > 5) {
          listaPlanesDetalles += `<div style="text-align: center; margin: 10px 0;"><small class="text-muted">... y ${planesActivosDetalle.length - 5} plan${planesActivosDetalle.length - 5 > 1 ? 'es' : ''} más</small></div>`
        }
      }

      const tieneImpacto = planesVinculados > 0

      const result = await this.$swal.fire({
        title: subfamilia.activo ? '¿Eliminar SubFamilia? (Etapa 1)' : '¿Eliminar SubFamilia Permanentemente? (Etapa 2)',
        html: `
          <div class="text-center">
            <p style="font-size: 1.1rem; margin-bottom: 15px;">
              ¿Estás seguro de que deseas ${subfamilia.activo ? 'desactivar' : 'eliminar permanentemente'} la subfamilia <strong>"${subfamilia.nombre}"</strong>?
            </p>
            
            ${subfamilia.activo ? `
              <div class="alert alert-info text-left" style="margin: 15px 0;">
                <i class="nc-icon nc-single-02"></i>
                <strong>🔄 Eliminación por Etapas:</strong>
                <ul style="margin: 8px 0 0 20px;">
                  <li><strong>Etapa 1 (actual):</strong> Eliminación lógica - se desactiva la subfamilia</li>
                  <li><strong>Etapa 2 (posterior):</strong> Eliminación física - se remueve permanentemente</li>
                </ul>
              </div>
            ` : `
              <div class="alert alert-danger text-left" style="margin: 15px 0;">
                <i class="nc-icon nc-simple-remove"></i>
                <strong>⚠️ Eliminación Permanente:</strong>
                <p style="margin: 8px 0;">Esta subfamilia ya está inactiva. Esta acción la eliminará <strong>permanentemente</strong> del sistema.</p>
              </div>
            `}
            
            <div class="alert ${tieneImpacto ? 'alert-danger' : 'alert-warning'} text-left" style="margin: 15px 0;">
              <i class="nc-icon nc-bell-55"></i>
              <strong>${tieneImpacto ? 'ATENCIÓN - Eliminación en Cascada:' : 'Información importante:'}</strong>
              <ul style="margin: 10px 0 0 20px;">
                ${subfamilia.activo ? '<li>La subfamilia será marcada como <strong>inactiva</strong></li>' : '<li>La subfamilia será <strong>eliminada permanentemente</strong></li>'}
                ${mensajePlanes}
                ${!tieneImpacto && subfamilia.activo ? '<li>Esta acción puede ser reversible desde la administración</li>' : ''}
              </ul>
            </div>
            
            ${listaPlanesDetalles ? `
              <div style="margin: 15px 0; max-height: 200px; overflow-y: auto; border: 1px solid #e0e0e0; border-radius: 8px; padding: 10px;">
                <h6 style="color: #dc3545; font-weight: 600; margin-bottom: 10px; text-align: center;">
                  <i class="nc-icon nc-bell-55"></i> Planes Activos que Bloquean la Eliminación:
                </h6>
                ${listaPlanesDetalles}
              </div>
            ` : ''}
            
            <p class="text-muted" style="font-size: 0.9rem;">
              ID de subfamilia: <strong>${subfamilia.id}</strong>
            </p>
          </div>
        `,
        icon: tieneImpacto ? 'error' : 'warning',
        showCancelButton: true,
        confirmButtonText: subfamilia.activo 
          ? (tieneImpacto ? 'Sí, desactivar todo (Etapa 1)' : 'Sí, desactivar subfamilia (Etapa 1)')
          : 'Sí, eliminar permanentemente (Etapa 2)',
        cancelButtonText: 'Cancelar',
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        reverseButtons: true,
        focusCancel: true,
        customClass: {
          container: 'swal-eliminar-subfamilia-cascada'
        }
      })
      
      if (result.isConfirmed) {
        this.procederConEliminacion(subfamilia)
      } else {
        // Limpiar loading si el usuario cancela
        this.loadingOperaciones.eliminar = null
      }
    },

    async procederConEliminacion(subfamilia) {
      let modalPlanesActivosMostrado = false // Variable de control para evitar cerrar el modal
      
      try {
        // Mostrar modal de loading inmediatamente después de la confirmación
        this.$swal.fire({
            title: 'Eliminando SubFamilia...',
            html: `
              <div class="text-center">
                <div class="spinner-border text-danger" style="width: 3rem; height: 3rem;" role="status">
                  <span class="sr-only">Eliminando...</span>
                </div>
                <p class="mt-3" style="font-size: 1.1rem;">
                  Procesando eliminación de <strong>"${subfamilia.nombre}"</strong>
                </p>
                <p class="text-muted" style="font-size: 0.9rem;">
                  Por favor espera mientras se completa la operación...
                </p>
              </div>
            `,
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            customClass: {
              container: 'swal-loading-eliminar'
            }
          })

          // Llamada real a la API de eliminación
          const response = await this.$CentralGymOPS_API({
            method: 'POST',
            url: '/',
            headers: {
              'Authorization': 'Bearer ' + sessionStorage.getItem('jCxbraoQeD')
            },
            data: {
              Header: { Operacion: '/v1/planes/subfamilia/eliminar' },
              Auth: { Token: sessionStorage.getItem('jCxbraoQeD') },
              Parameters: { id: subfamilia.id },
              Body: {}
            }
          })

          console.log('🗑️ Respuesta del servidor:', response.data)

          // Verificar respuesta exitosa según especificación
          if (response.data?.result?.code === 0 && response.data?.result?.httpCode === 200) {
            const subfamiliaEliminada = response.data?.dataOut?.subfamilia
            const mensaje = response.data?.dataOut?.mensaje || 'SubFamilia eliminada exitosamente'
            
            await this.$swal.fire({
              title: '¡SubFamilia Eliminada!',
              html: `
                <div class="text-center">
                  <i class="nc-icon nc-check-2" style="font-size: 3rem; color: #6bd098; margin-bottom: 15px;"></i>
                  <h5 style="color: #28a745; margin-bottom: 15px;">¡Etapa 1 Completada!</h5>
                  
                  <div class="alert alert-success text-left" style="margin: 15px 0;">
                    <strong>✅ SubFamilia desactivada:</strong>
                    <ul style="margin: 5px 0 0 20px;">
                      <li>"${subfamiliaEliminada ? subfamiliaEliminada.nombre : subfamilia.nombre}" (ID: ${subfamiliaEliminada ? subfamiliaEliminada.id : subfamilia.id})</li>
                    </ul>
                  </div>
                  
                  <div style="margin-top: 15px; padding: 10px; background: rgba(40, 167, 69, 0.1); border-radius: 4px;">
                    <small class="text-muted">
                      <strong>💡 Etapa 1 Completada:</strong> Eliminación lógica exitosa. El registro se conserva en la base de datos pero está marcado como inactivo.
                    </small>
                  </div>
                  
                  <div style="margin-top: 10px; padding: 10px; background: rgba(255, 193, 7, 0.1); border-radius: 4px; border-left: 4px solid #ffc107;">
                    <small class="text-muted">
                      <strong>🔄 Próximo Paso:</strong> Puedes realizar una segunda eliminación para remover permanentemente la subfamilia del sistema.
                    </small>
                  </div>
                </div>
              `,
              icon: 'success',
              showConfirmButton: true,
              confirmButtonText: 'Entendido',
              confirmButtonClass: 'btn btn-success',
              toast: false,
              position: 'center',
              allowOutsideClick: false,
              allowEscapeKey: false,
              customClass: {
                container: 'swal-eliminacion-exitosa'
              }
            })

            // Recargar SubFamilias y actualizar estadísticas DESPUÉS de que el usuario cierre el modal
            await this.cargarSubFamilias()
            this.$emit('refresh')
            
          } else if (response.data?.result?.code === 409 && response.data?.result?.httpCode === 409) {
            // Conflicto: Planes activos impiden eliminación física
            const planesActivos = response.data?.dataOut?.planesActivos || []
            const resumen = response.data?.dataOut?.resumen || {}
            const accionesRequeridas = response.data?.dataOut?.accionesRequeridas || []
            
            let planesTotalTexto = `${resumen.totalPlanes || planesActivos.length} plan${(resumen.totalPlanes || planesActivos.length) > 1 ? 'es' : ''}`
            
            // Construir lista de planes activos (máximo 5 para no sobrecargar el modal)
            let listaPlanes = ''
            const planesParaMostrar = planesActivos.slice(0, 5)
            planesParaMostrar.forEach(plan => {
              listaPlanes += `
                <div style="margin: 8px 0; padding: 8px; background: rgba(220, 53, 69, 0.1); border-radius: 4px; border-left: 3px solid #dc3545;">
                  <strong>${plan.descripcion}</strong><br>
                  <small>ID: ${plan.id} | Familia: ${plan.familia} | Valor: $${plan.valor}</small>
                </div>
              `
            })
            
            if (planesActivos.length > 5) {
              listaPlanes += `<div style="text-align: center; margin: 10px 0;"><small class="text-muted">... y ${planesActivos.length - 5} plan${planesActivos.length - 5 > 1 ? 'es' : ''} más</small></div>`
            }
            
            // Construir mensaje de acciones requeridas
            let accionesTexto = ''
            accionesRequeridas.forEach(accion => {
              accionesTexto += `<li>${accion.descripcion}</li>`
            })
            
            this.$swal.fire({
              title: 'Eliminación Física Bloqueada',
              html: `
                <div class="text-center">
                  <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #ffc107; margin-bottom: 15px;"></i>
                  <h5 style="color: #856404; margin-bottom: 15px;">¡Planes Activos Detectados!</h5>
                  
                  <div class="alert alert-warning text-left" style="margin: 15px 0;">
                    <strong>⚠️ No se puede eliminar permanentemente:</strong>
                    <p style="margin: 8px 0;">La subfamilia tiene <strong>${planesTotalTexto} activo${(resumen.totalPlanes || planesActivos.length) > 1 ? 's' : ''}</strong> que debe${(resumen.totalPlanes || planesActivos.length) > 1 ? 'n' : ''} ser desactivado${(resumen.totalPlanes || planesActivos.length) > 1 ? 's' : ''} primero.</p>
                  </div>
                  
                  <div style="margin-top: 15px; max-height: 200px; overflow-y: auto;">
                    <h6 style="color: #856404;">Planes que requieren atención:</h6>
                    ${listaPlanes}
                  </div>
                  
                  ${accionesTexto ? `
                    <div class="alert alert-info text-left" style="margin: 15px 0;">
                      <strong>📋 Acciones requeridas:</strong>
                      <ul style="margin: 8px 0 0 20px;">
                        ${accionesTexto}
                      </ul>
                    </div>
                  ` : ''}
                  
                  <div style="margin-top: 15px; padding: 10px; background: rgba(255, 193, 7, 0.1); border-radius: 4px;">
                    <small class="text-muted">
                      <strong>💡 Sugerencia:</strong> Desactiva o reasigna los planes listados, luego intenta eliminar la subfamilia nuevamente.
                    </small>
                  </div>
                </div>
              `,
              icon: 'warning',
              showConfirmButton: true,
              confirmButtonText: 'Entendido',
              confirmButtonClass: 'btn btn-warning',
              allowOutsideClick: false,
              allowEscapeKey: false,
              customClass: {
                container: 'swal-planes-activos'
              }
            })
            
            // Recargar la lista para reflejar cualquier cambio
            await this.cargarSubFamilias()
            
          } else {
            // Otros errores del servidor
            throw new Error(response.data?.result?.message || 'Error desconocido del servidor')
          }
          
        } catch (error) {
          console.error('❌ Error al eliminar subfamilia:', error)
          
          let errorMessage = 'No se pudo eliminar la subfamilia'
          let errorTitle = 'Error al Eliminar'
          
          if (error.response?.status === 409) {
            // Conflicto: Planes activos (manejar como caso especial si llega por excepción)
            console.log('🔍 Debug error 409:', error.response.data)
            
            if (error.response.data?.dataOut?.planesActivos) {
              const planesActivos = error.response.data.dataOut.planesActivos || []
              const resumen = error.response.data.dataOut.resumen || {}
              const accionesRequeridas = error.response.data.dataOut.accionesRequeridas || []
              
              let planesTotalTexto = `${resumen.totalPlanes || planesActivos.length} plan${(resumen.totalPlanes || planesActivos.length) > 1 ? 'es' : ''}`
              
              // Construir lista de planes activos (máximo 5 para no sobrecargar el modal)
              let listaPlanes = ''
              const planesParaMostrar = planesActivos.slice(0, 5)
              planesParaMostrar.forEach(plan => {
                listaPlanes += `
                  <div style="margin: 8px 0; padding: 8px; background: rgba(220, 53, 69, 0.1); border-radius: 4px; border-left: 3px solid #dc3545;">
                    <strong>${plan.descripcion}</strong><br>
                    <small>ID: ${plan.id} | Familia: ${plan.familia} | Valor: $${plan.valor}</small>
                  </div>
                `
              })
              
              if (planesActivos.length > 5) {
                listaPlanes += `<div style="text-align: center; margin: 10px 0;"><small class="text-muted">... y ${planesActivos.length - 5} plan${planesActivos.length - 5 > 1 ? 'es' : ''} más</small></div>`
              }
              
              // Construir mensaje de acciones requeridas
              let accionesTexto = ''
              accionesRequeridas.forEach(accion => {
                accionesTexto += `<li>${accion.descripcion}</li>`
              })
              
              this.$swal.fire({
                title: 'Eliminación Física Bloqueada',
                html: `
                  <div class="text-center">
                    <i class="nc-icon nc-simple-remove" style="font-size: 3rem; color: #ffc107; margin-bottom: 15px;"></i>
                    <h5 style="color: #856404; margin-bottom: 15px;">¡Planes Activos Detectados!</h5>
                    
                    <div class="alert alert-warning text-left" style="margin: 15px 0;">
                      <strong>⚠️ No se puede eliminar permanentemente:</strong>
                      <p style="margin: 8px 0;">La subfamilia tiene <strong>${planesTotalTexto} activo${(resumen.totalPlanes || planesActivos.length) > 1 ? 's' : ''}</strong> que debe${(resumen.totalPlanes || planesActivos.length) > 1 ? 'n' : ''} ser desactivado${(resumen.totalPlanes || planesActivos.length) > 1 ? 's' : ''} primero.</p>
                    </div>
                    
                    <div style="margin-top: 15px; max-height: 200px; overflow-y: auto;">
                      <h6 style="color: #856404;">Planes que requieren atención:</h6>
                      ${listaPlanes}
                    </div>
                    
                    ${accionesTexto ? `
                      <div class="alert alert-info text-left" style="margin: 15px 0;">
                        <strong>📋 Acciones requeridas:</strong>
                        <ul style="margin: 8px 0 0 20px;">
                          ${accionesTexto}
                        </ul>
                      </div>
                    ` : ''}
                    
                    <div style="margin-top: 15px; padding: 10px; background: rgba(255, 193, 7, 0.1); border-radius: 4px;">
                      <small class="text-muted">
                        <strong>💡 Sugerencia:</strong> Desactiva o reasigna los planes listados, luego intenta eliminar la subfamilia nuevamente.
                      </small>
                    </div>
                  </div>
                `,
                icon: 'warning',
                showConfirmButton: true,
                confirmButtonText: 'Entendido',
                confirmButtonClass: 'btn btn-warning',
                allowOutsideClick: false,
                allowEscapeKey: false,
                customClass: {
                  container: 'swal-planes-activos'
                }
              })
              
              // Recargar la lista para reflejar cualquier cambio
              await this.cargarSubFamilias()
              modalPlanesActivosMostrado = true // Marcar que se mostró el modal
              return // Salir aquí para no mostrar el modal de error genérico
            } else {
              errorMessage = 'Conflicto al eliminar la subfamilia. Puede tener dependencias activas.'
              errorTitle = 'Conflicto de Eliminación'
            }
          } else if (error.response?.status === 401) {
            errorMessage = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
            errorTitle = 'Sesión Expirada'
            setTimeout(() => {
              sessionStorage.clear()
              this.$router.push('/login')
            }, 2000)
          } else if (error.response?.status === 403) {
            errorMessage = 'No tienes permisos para eliminar esta subfamilia.'
            errorTitle = 'Acceso Denegado'
          } else if (error.message) {
            errorMessage = error.message
          }
          
          this.$swal.fire({
            title: errorTitle,
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'Entendido',
            confirmButtonClass: 'btn btn-danger'
          })
        } finally {
          this.loadingOperaciones.eliminar = null
          // Solo cerrar el modal de loading si NO se mostró el modal de planes activos
          if (!modalPlanesActivosMostrado) {
            this.$swal.close() // Cerrar el modal de loading
          }
        }
    },

    verDetalle(subfamilia) {
      this.loadingOperaciones.verDetalle = subfamilia.id
      
      // Simular un delay pequeño para mostrar el loading
      setTimeout(() => {
        const detalleHtml = '<div class="text-left">' +
          '<p><strong>ID:</strong> ' + subfamilia.id + '</p>' +
          '<p><strong>Nombre:</strong> ' + subfamilia.nombre + '</p>' +
          '<p><strong>Familia:</strong> ' + this.getFamiliaSeleccionadaNombre() + '</p>' +
          '<p><strong>Estado:</strong> ' + (subfamilia.activo ? 'Activo' : 'Inactivo') + '</p>' +
          '<p><strong>Creado por:</strong> ' + (subfamilia.nombreUsuarioCreacion || 'N/A') + '</p>' +
          '<p><strong>Fecha de creación:</strong> ' + this.formatearFecha(subfamilia.createdAt) + '</p>' +
          '</div>'
        
        this.$swal.fire({
          title: subfamilia.nombre,
          html: detalleHtml,
          confirmButtonText: 'Cerrar',
          width: '500px'
        })
        
        this.loadingOperaciones.verDetalle = null
      }, 300)
    },

    async exportarExcel() {
      try {
        this.loadingOperaciones.exportar = true
        
        this.$swal.fire({
          title: 'Exportando...',
          text: 'Generando archivo Excel con todas las subfamilias',
          allowOutsideClick: false,
          didOpen: () => {
            this.$swal.showLoading()
          }
        })

        // Simular exportación - aquí iría la implementación real
        setTimeout(() => {
          this.$swal.fire({
            title: '¡Exportado!',
            text: 'El archivo Excel se ha descargado exitosamente',
            icon: 'success',
            timer: 3000,
            showConfirmButton: false
          })
          this.loadingOperaciones.exportar = false
        }, 2000)

      } catch (error) {
        console.error('Error al exportar:', error)
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudo exportar el archivo Excel',
          icon: 'error'
        })
        this.loadingOperaciones.exportar = false
      }
    },

    // Utils
    mostrarModal() {
      this.$nextTick(() => {
        const modalElement = this.$refs.modalSubFamilia;
        console.log('🔍 Debug modal:', {
          modalElement: modalElement,
          bootstrap5: window.bootstrap && window.bootstrap.Modal
        });
        
        if (modalElement) {
          if (window.bootstrap && window.bootstrap.Modal) {
            // Bootstrap 5 nativo
            console.log('✅ Usando Bootstrap 5 modal');
            const modal = new window.bootstrap.Modal(modalElement);
            modal.show();
          } else {
            // Fallback manual con JavaScript puro
            console.log('⚠️ Usando fallback manual para modal');
            modalElement.style.display = 'block';
            modalElement.classList.add('show');
            document.body.classList.add('modal-open');
            
            // Crear backdrop manualmente
            const backdrop = document.createElement('div');
            backdrop.className = 'modal-backdrop fade show';
            backdrop.id = 'modal-backdrop-subfamilia';
            document.body.appendChild(backdrop);
          }
        } else {
          console.error('❌ No se encontró el elemento modal');
        }
      });
    },

    getFamiliaSeleccionadaNombre() {
      if (!this.familiaSeleccionada) return 'N/A'
      
      // Si hay contexto de navegación, usar esa información
      if (this.contextoNavegacion && this.contextoNavegacion.familiaOrigen._id === this.familiaSeleccionada) {
        return this.contextoNavegacion.familiaOrigen.nombre
      }
      
      // Buscar en el catálogo de familias
      const familia = this.catalogos.familias.find(f => f._id === this.familiaSeleccionada)
      return familia ? familia.nombre : 'N/A'
    },

    getFamiliaSeleccionadaId() {
      if (!this.familiaSeleccionada) return 'N/A'
      
      // Si hay contexto de navegación, usar esa información
      if (this.contextoNavegacion && this.contextoNavegacion.familiaOrigen._id === this.familiaSeleccionada) {
        return this.contextoNavegacion.familiaOrigen.id
      }
      
      // Buscar en el catálogo de familias
      const familia = this.catalogos.familias.find(f => f._id === this.familiaSeleccionada)
      return familia ? familia.id : 'N/A'
    },

    formatearFecha(fecha) {
      if (!fecha) return 'N/A'
      
      const date = new Date(fecha)
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    navegarAPlanes(subfamilia) {
      console.log('🔄 Navegando a Planes con subfamilia:', subfamilia.nombre)
      
      // Determinar información de la familia (si está disponible)
      let familiaInfo = null
      
      if (this.familiaSeleccionada) {
        // Si hay familia seleccionada, usarla
        familiaInfo = this.catalogos.familias.find(f => f._id === this.familiaSeleccionada)
      } else {
        // Si no hay familia seleccionada, obtener la familia de la subfamilia
        const familiaId = (subfamilia.familia && subfamilia.familia._id) || (subfamilia.familiaInfo && subfamilia.familiaInfo._id)
        if (familiaId) {
          familiaInfo = this.catalogos.familias.find(f => f._id === familiaId)
        }
        
        // Si no se encuentra en el catálogo, usar la información directa de la subfamilia
        if (!familiaInfo && (subfamilia.familia || subfamilia.familiaInfo)) {
          familiaInfo = {
            _id: (subfamilia.familia && subfamilia.familia._id) || 'unknown',
            id: (subfamilia.familia && subfamilia.familia.id) || (subfamilia.familiaInfo && subfamilia.familiaInfo.id) || 0,
            nombre: (subfamilia.familia && subfamilia.familia.nombre) || (subfamilia.familiaInfo && subfamilia.familiaInfo.nombre) || 'Familia Desconocida'
          }
        }
      }
      
      // Construir parámetros de navegación con información disponible
      const parametrosNavegacion = {
        subfamiliaId: subfamilia._id,              // MongoDB _id para la consulta API
        subfamiliaIdNumerico: subfamilia.id,       // ID numérico para display
        subfamiliaNombre: subfamilia.nombre,
        tab: 'planes'
      }
      
      // Agregar información de familia si está disponible
      if (familiaInfo && familiaInfo._id !== 'unknown') {
        parametrosNavegacion.familiaId = familiaInfo._id        // MongoDB _id para la consulta API
        parametrosNavegacion.familiaIdNumerico = familiaInfo.id // ID numérico para display
        parametrosNavegacion.familiaNombre = familiaInfo.nombre
      }
      
      console.log('🎯 Parámetros de navegación:', parametrosNavegacion)
      
      // Emitir evento al componente padre para cambiar de pestaña
      this.$emit('navegarAPlanes', parametrosNavegacion)
      
      // Mostrar confirmación contextual
      let textoConfirmacion = ''
      if (familiaInfo && familiaInfo._id !== 'unknown') {
        textoConfirmacion = `Accediendo a los planes de "${subfamilia.nombre}" en la familia "${familiaInfo.nombre}"`
      } else {
        textoConfirmacion = `Accediendo a los planes de la subfamilia "${subfamilia.nombre}"`
        console.log('ℹ️ Navegando solo con información de subfamilia (familia no disponible en catálogo)')
      }
      
      this.$swal.fire({
        title: 'Navegando...',
        text: textoConfirmacion,
        icon: 'info',
        timer: 1500,
        showConfirmButton: false,
        toast: true,
        position: 'top-end'
      })
    },

    emitirCambiarTab() {
      this.$emit('cambiarTab', 'familias')
    },

    limpiarContextoNavegacion() {
      console.log('🧹 Limpiando contexto de navegación y filtros desde SubFamilias')
      // Limpiar familia seleccionada para mostrar vista global
      this.familiaSeleccionada = ''
      // Limpiar filtros
      this.limpiarFiltros()
      // Emitir evento para limpiar contexto en el componente padre
      this.$emit('limpiarContexto')
    }
  }
}
</script>

<style scoped>
.subfamilias-manager {
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.manager-header {
  background: white;
  padding: 25px;
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.manager-title {
  color: #2c3e50;
  margin-bottom: 5px;
}

.familia-info {
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%);
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.table-section .card {
  border: none;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.form-label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
  font-size: 0.9rem;
}

.empty-icon {
  font-size: 4rem;
  color: #dee2e6;
}

.empty-state {
  padding: 3rem 1rem;
}

.modal-content {
  border-radius: 12px;
  border: none;
  box-shadow: 0 10px 40px rgba(0,0,0,0.2);
}

.modal-header {
  border-bottom: 1px solid #f0f0f0;
  border-radius: 12px 12px 0 0;
}

.form-control:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.badge-outline-info {
  color: #17a2b8;
  border: 1px solid #17a2b8;
  background-color: transparent;
}

.subfamilia-info strong {
  color: #2c3e50;
}

.filters-section .card {
  border: none;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.btn-sort {
  background: none;
  border: none;
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 0;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.btn-sort:hover {
  color: #667eea;
}

.pagination-section .pagination {
  margin-bottom: 0;
}

.pagination-section .page-link {
  border-radius: 6px;
  margin: 0 2px;
  border: 1px solid #dee2e6;
}

.pagination-section .page-item.active .page-link {
  background-color: #667eea;
  border-color: #667eea;
}

/* Estilos para modal de eliminación exitosa */
:deep(.swal-eliminacion-exitosa) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 550px;
}

:deep(.swal-eliminacion-exitosa) .swal2-html-container {
  margin: 0;
  padding: 0;
  text-align: left;
}

:deep(.swal-eliminacion-exitosa) .alert-success {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
  margin: 15px 0;
}

:deep(.swal-eliminacion-exitosa) h5 {
  text-align: center;
  margin-bottom: 20px;
}

:deep(.swal-eliminacion-exitosa) .swal2-confirm {
  background: #28a745 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
}

:deep(.swal-eliminacion-exitosa) .swal2-timer-progress-bar {
  background: rgba(40, 167, 69, 0.8) !important;
}

/* Estilos para modal de loading de eliminación */
:deep(.swal-loading-eliminar) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 500px;
}

:deep(.swal-loading-eliminar) .swal2-title {
  color: #dc3545;
  font-weight: 700;
  margin-bottom: 20px;
}

:deep(.swal-loading-eliminar) .swal2-html-container {
  margin: 0;
  padding: 0;
}

:deep(.swal-loading-eliminar) .spinner-border {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Estilos para modal de confirmación de eliminación */
:deep(.swal-eliminar-subfamilia-cascada) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 600px;
}

:deep(.swal-eliminar-subfamilia-cascada) .swal2-title {
  color: #dc3545;
  font-weight: 700;
  margin-bottom: 20px;
}

:deep(.swal-eliminar-subfamilia-cascada) .swal2-html-container {
  margin: 1.5rem 0;
  text-align: left;
}

:deep(.swal-eliminar-subfamilia-cascada) .alert-danger {
  border-color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

:deep(.swal-eliminar-subfamilia-cascada) .alert-info {
  border-color: #17a2b8;
  background-color: rgba(23, 162, 184, 0.1);
}

:deep(.swal-eliminar-subfamilia-cascada) .alert-warning {
  border-color: #ffc107;
  background-color: rgba(255, 193, 7, 0.1);
}

/* Estilos para modal de planes activos */
:deep(.swal-planes-activos) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 650px;
}

:deep(.swal-planes-activos) .swal2-title {
  color: #856404;
  font-weight: 700;
  margin-bottom: 20px;
}

:deep(.swal-planes-activos) .swal2-html-container {
  margin: 0;
  padding: 0;
  text-align: left;
}

:deep(.swal-planes-activos) .alert-warning {
  border-color: #ffc107;
  background-color: rgba(255, 193, 7, 0.1);
  margin: 15px 0;
}

:deep(.swal-planes-activos) .alert-info {
  border-color: #17a2b8;
  background-color: rgba(23, 162, 184, 0.1);
  margin: 15px 0;
}

:deep(.swal-planes-activos) h5 {
  text-align: center;
  margin-bottom: 20px;
}

:deep(.swal-planes-activos) h6 {
  color: #856404;
  font-weight: 600;
  margin-bottom: 10px;
}

:deep(.swal-planes-activos) .swal2-confirm {
  background: #ffc107 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
  color: #212529 !important;
}

/* Estilos para modal de subfamilia exitosa */
:deep(.swal-subfamilia-exitosa) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 600px;
}

:deep(.swal-subfamilia-exitosa) .swal2-html-container {
  margin: 0;
  padding: 0;
  text-align: left;
}

:deep(.swal-subfamilia-exitosa) .alert-success {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.1);
  margin: 15px 0;
  border-radius: 8px;
}

:deep(.swal-subfamilia-exitosa) h5 {
  text-align: center;
  margin-bottom: 20px;
  font-weight: 700;
}

:deep(.swal-subfamilia-exitosa) .swal2-confirm {
  background: #28a745 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

:deep(.swal-subfamilia-exitosa) .swal2-confirm:hover {
  background: #218838 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
}

/* Estilos para navegación a Planes */
.subfamilia-nombre-clickeable {
  cursor: pointer;
  color: #667eea;
  transition: color 0.3s ease;
}

.subfamilia-nombre-clickeable:hover {
  color: #764ba2;
  text-decoration: underline;
}

.planes-link {
  cursor: pointer;
  color: #28a745;
  transition: color 0.3s ease;
}

.planes-link:hover {
  color: #1e7e34;
  text-decoration: underline;
}

/* Estilos para vista global de subfamilias */
.familia-badge {
  font-weight: 600;
  color: #495057;
}

.familia-badge.text-danger {
  color: #dc3545 !important;
  font-weight: 700;
}

.alert-info {
  border-color: #bee5eb;
  background-color: #d1ecf1;
  color: #0c5460;
}

.alert-success {
  border-color: #c3e6cb;
  background-color: #d4edda;
  color: #155724;
}

/* Estilos para banner de contexto de navegación */
.familia-origen-badge {
  font-weight: 700;
  font-size: 1.1rem;
}

.alert-primary {
  border-color: #b3d7ff;
  background-color: #cce7ff;
  color: #004085;
}

.alert-warning {
  border-color: #ffeaa7;
  background-color: #fff3cd;
  color: #856404;
}

.alert-warning .text-danger {
  font-weight: 600;
}

/* Estilos para modal de subfamilia duplicada */
:deep(.swal-subfamilia-duplicada) .swal2-popup {
  border-radius: 15px;
  padding: 2rem;
  max-width: 650px;
}

:deep(.swal-subfamilia-duplicada) .swal2-title {
  color: #f39c12;
  font-weight: 700;
  margin-bottom: 20px;
}

:deep(.swal-subfamilia-duplicada) .swal2-html-container {
  margin: 1.5rem 0;
  text-align: left;
}

:deep(.swal-subfamilia-duplicada) .swal2-confirm {
  background: #f39c12 !important;
  border: none !important;
  border-radius: 8px !important;
  padding: 10px 25px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
  color: white !important;
}

:deep(.swal-subfamilia-duplicada) .swal2-confirm:hover {
  background: #e67e22 !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(243, 156, 18, 0.4);
}
</style> 